"use client";

import { useTranslations } from "@/hooks/use-translations";
import { getFormattedBookingDay, getFormattedBookingTime } from "@/lib/booking.utils";
import { Config } from "@/shared/config";
import { Accordion, AccordionItem } from "@heroui/react";
import Script from "next/script";

interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

export default function Faq() {
  const t = useTranslations();

  if (!t) return null;


  const getFormattedOpeningHours = () => {
    return `${Config.MORNING_SLOT.start_time}-${Config.AFTERNOON_SLOT.end_time}`;
  };

  // Replace placeholders in FAQ answers with actual config values
  const replaceConfigPlaceholders = (text: string) => {
    if (!text) return text;
    return text
      .replace(/dal lunedì al venerdì/g, 'dal lunedì alla domenica')
      .replace(/dalle 8:00 alle 20:00/g, `dalle ${getFormattedOpeningHours()}`)
      .replace(/Lun-Ven/g, 'Lun-Dom')
      .replace(/€150/g, `€${Config.MEMBERSHIP_FULL_MONTHLY_PRICE}`)
      .replace(/€100/g, `€${Config.MEMBERSHIP_DISCOUNT_MONTHLY_PRICE}`)
      .replace(/\\{\\{bookingDay\\}\\}/g, getFormattedBookingDay())
      .replace(/\\{\\{bookingTime\\}\\}/g, getFormattedBookingTime());
  };

  const faqItems: FaqItem[] = t.faq.questions.map((item: { question: string; answer: string }, index: number) => ({
    id: `faq-${index}`,
    question: item.question,
    answer: replaceConfigPlaceholders(item.answer),
  }));

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map((item: FaqItem) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer,
      },
    })),
  };

  return (
    <>
      <Script id="faq-schema" type="application/ld+json">
        {JSON.stringify(schemaData)}
      </Script>
      <section id="faq" className="flex flex-col gap-8 items-center justify-center w-full min-h-[400px] px-4">
        <div className="text-center max-w-3xl">
          <h2 className="font-bold mb-4 text-3xl desktop:text-4xl">
            {t.faq.title}
          </h2>
          <p className="text-foreground/75 text-lg desktop:text-xl">
            {t.faq.subtitle}
          </p>
        </div>

        <Accordion
          variant="shadow"
          className="max-w-4xl w-full"
          selectionMode="multiple"
        >
          {faqItems.map((item: FaqItem) => (
            <AccordionItem
              key={item.id}
              aria-label={item.question}
              title={
                <span className="text-left font-medium text-lg">
                  {item.question}
                </span>
              }
              className="py-2"
            >
              <div className="text-default-700 pb-4 leading-relaxed">
                <p dangerouslySetInnerHTML={{ __html: item.answer }} />
              </div>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </>
  );
}
